import Dexie, { type Table } from 'dexie'
import { DB_NAME } from '@utils/constants.js'

export interface ObraConfig {
  id: 1
  nome: string
  dataInicio: string
  areaTotal: number
  padraoConstrucao: string
  regiao: string
  orcamentoTotal: number
  faseAtual: string
  faseConstrutiva?: string
  tipoContratacao: string
  orcamentoPorCategoria?: Record<string, number>
  valorMinimoNF?: number | undefined
  dataAlvara?: string | undefined
  /** Percentual do orçamento reservado para imprevistos (recomendado 5–10%). */
  reservaContingenciaPct?: number | undefined
  /** Data prevista de término da obra (ISO yyyy-mm-dd). */
  dataPrevisaoTermino?: string | undefined
  criadoEm: string
  atualizadoEm: string
}

export interface ChecklistItem {
  id: string
  texto: string
  concluido: boolean
}

export interface TopicoProgresso {
  id?: number
  topicoId: string
  moduloId: string
  concluido: boolean
  favorito?: boolean
  percentualPratico: number
  anotacoes: string
  checklistItems: ChecklistItem[]
  metadados?: Record<string, string>
  atualizadoEm: string
}

export interface LancamentoFinanceiro {
  id?: number
  tipo?: 'gasto' | 'receita'
  titulo: string
  valor: number
  categoria: string
  status: string
  formaPagamento: string
  data: string
  moduloId?: string
  fornecedor?: string
  documentoId?: number
  observacoes?: string
  criadoEm: string
  atualizadoEm: string
}

export type DocumentoStorageBackend = 'opfs' | 'indexeddb'

export interface DocumentoArquivo {
  id: number
  blob: Blob
}

export interface Documento {
  id?: number
  nome: string
  categoria: string
  tipo: string
  tamanho?: number
  caminhoArquivo?: string
  topicoId?: string
  lancamentoId?: number
  moduloId?: string
  fornecedor?: string
  valor?: number
  hasArquivo?: boolean
  arquivoBackend?: DocumentoStorageBackend
  data: string
  observacoes?: string
  criadoEm: string
}

export interface Alerta {
  id?: number
  tipo: 'info' | 'warning' | 'danger' | 'success'
  titulo: string
  mensagem: string
  lido: boolean
  status?: 'ativo' | 'resolvido' | 'ignorado'
  chave?: string
  dataVencimento?: string
  criadoEm: string
}

export type DiarioClima = 'sol' | 'nublado' | 'chuva' | 'chuva-forte'

export interface DiarioEntry {
  id?: number
  topicoId?: string
  texto: string
  /** Condição do tempo no dia (afeta cronograma e justifica paradas). */
  clima?: DiarioClima | undefined
  /** Ids de contatos que trabalharam no dia. */
  equipeIds?: number[] | undefined
  /** Ids de documentos (categoria "Fotos de Obra") anexados à anotação. */
  fotoIds?: number[] | undefined
  /** Contrato relacionado (ex.: registro de medição de parcela). */
  contratoId?: number | undefined
  criadoEm: string
  atualizadoEm: string
}

export type ObraPendenciaTipo = 'pendencia' | 'retrabalho'
export type ObraPendenciaStatus = 'aberta' | 'em-andamento' | 'resolvida'
export type ObraPendenciaPrioridade = 'baixa' | 'media' | 'alta'

export interface ObraPendencia {
  id?: number
  tipo: ObraPendenciaTipo
  titulo: string
  descricao?: string
  status: ObraPendenciaStatus
  prioridade: ObraPendenciaPrioridade
  faseConstrutiva?: string
  topicoId?: string
  retrabalhoRefId?: string
  responsavel?: string
  prazo?: string
  custoEstimado?: number
  criadoEm: string
  atualizadoEm: string
  resolvidoEm?: string
}

export type OrcamentoMaterialStatus = 'rascunho' | 'cotado' | 'escolhido' | 'comprado'

export interface OrcamentoMaterial {
  id?: number
  material: string
  fornecedor: string
  descricao: string
  valorTotal: number
  data: string
  status: OrcamentoMaterialStatus
  grupoId: string
  documentoId?: number
  /** Lançamento em Financeiro › Materiais (previsto na escolha, pago na compra). */
  lancamentoId?: number | undefined
  categoria?: string
  observacoes?: string
  criadoEm: string
  atualizadoEm: string
}

// ── Gestão da obra: contatos, contratos, cronograma ──────────

export type ContatoPapel =
  | 'pedreiro'
  | 'empreiteiro'
  | 'mestre-de-obras'
  | 'eletricista'
  | 'hidraulico'
  | 'pintor'
  | 'carpinteiro'
  | 'engenheiro'
  | 'arquiteto'
  | 'fornecedor'
  | 'outro'

export interface Contato {
  id?: number
  nome: string
  papel: ContatoPapel
  telefone?: string | undefined
  empresa?: string | undefined
  /** CPF ou CNPJ (opcional, útil para contrato e NF). */
  documento?: string | undefined
  avaliacao?: 1 | 2 | 3 | 4 | 5 | undefined
  observacoes?: string | undefined
  criadoEm: string
  atualizadoEm: string
}

export type ContratoTipo = 'empreitada-global' | 'empreitada-medicao' | 'diaria' | 'administracao'
export type ContratoStatus = 'ativo' | 'concluido' | 'cancelado'

export interface Contrato {
  id?: number
  titulo: string
  /** Contato vinculado (prestador). Nome copiado em `prestador` para histórico. */
  contatoId?: number | undefined
  prestador: string
  tipo: ContratoTipo
  escopo?: string | undefined
  valorTotal: number
  dataInicio?: string | undefined
  dataFimPrevista?: string | undefined
  /** Percentual retido até a vistoria final (recomendado 10–15%). */
  retencaoPct?: number | undefined
  faseConstrutiva?: string | undefined
  documentoId?: number | undefined
  status: ContratoStatus
  observacoes?: string | undefined
  criadoEm: string
  atualizadoEm: string
}

export type ParcelaCondicao = 'sinal' | 'medicao' | 'data' | 'retencao' | 'aditivo'
export type ParcelaStatus = 'prevista' | 'liberada' | 'paga'

export interface MedicaoItem {
  texto: string
  ok: boolean
}

/** Vistoria feita no canteiro antes de liberar a parcela: checklist + fotos como prova. */
export interface ParcelaMedicao {
  data: string
  itens: MedicaoItem[]
  observacoes?: string | undefined
  /** Ids de documentos (categoria "Fotos de Obra"). */
  fotoIds?: number[] | undefined
  /** Anotação criada no diário como registro da medição. */
  diarioId?: number | undefined
}

export interface ContratoParcela {
  id?: number
  contratoId: number
  ordem: number
  descricao: string
  valor: number
  condicao: ParcelaCondicao
  status: ParcelaStatus
  dataPrevista?: string | undefined
  /** Data em que o serviço foi medido/aceito (libera o pagamento). */
  liberadaEm?: string | undefined
  medicao?: ParcelaMedicao | undefined
  dataPagamento?: string | undefined
  /** Lançamento financeiro gerado ao pagar. */
  lancamentoId?: number | undefined
  observacoes?: string | undefined
  criadoEm: string
  atualizadoEm: string
}

/** Escolha do dono que trava a obra (sistema, vão, prumada). */
export interface Decisao {
  id?: number
  /** Chave do catálogo, se veio de uma decisão sugerida. */
  chave?: string | undefined
  titulo: string
  escolha: string
  faseId: string
  travaEm?: string | undefined
  motivo?: string | undefined
  travada: boolean
  criadoEm: string
  atualizadoEm: string
}

export interface CronogramaFase {
  id?: number
  /** Id em FASES_CONSTRUTIVAS. */
  faseId: string
  inicioPrevisto?: string | undefined
  fimPrevisto?: string | undefined
  inicioReal?: string | undefined
  fimReal?: string | undefined
  /** 0–100. */
  avancoPct: number
  orcamentoPrevisto?: number | undefined
  observacoes?: string | undefined
  atualizadoEm: string
}

class MinhaObraDB extends Dexie {
  obra_config!: Table<ObraConfig, 1>
  topicos_progresso!: Table<TopicoProgresso, number>
  lancamentos_financeiros!: Table<LancamentoFinanceiro, number>
  documentos!: Table<Documento, number>
  documento_arquivos!: Table<DocumentoArquivo, number>
  alertas!: Table<Alerta, number>
  diario!: Table<DiarioEntry, number>
  orcamentos_materiais!: Table<OrcamentoMaterial, number>
  obra_pendencias!: Table<ObraPendencia, number>
  contatos!: Table<Contato, number>
  contratos!: Table<Contrato, number>
  contrato_parcelas!: Table<ContratoParcela, number>
  cronograma_fases!: Table<CronogramaFase, number>
  decisoes!: Table<Decisao, number>

  constructor() {
    super(DB_NAME)
    this.version(1).stores({
      obra_config: 'id',
      topicos_progresso: '++id, topicoId, moduloId, concluido',
      lancamentos_financeiros: '++id, categoria, status, data, criadoEm',
      documentos: '++id, categoria, topicoId, lancamentoId, criadoEm',
      alertas: '++id, tipo, lido, criadoEm',
      diario: '++id, topicoId, criadoEm',
    })
    this.version(2).stores({
      alertas: '++id, tipo, lido, status, chave, criadoEm',
    })
    this.version(3).stores({
      documento_arquivos: 'id',
    })
    this.version(4).stores({
      orcamentos_materiais: '++id, grupoId, status, material, criadoEm',
    })
    this.version(5).stores({
      obra_pendencias: '++id, tipo, status, prioridade, faseConstrutiva, topicoId, criadoEm',
    })
    this.version(6).stores({
      contatos: '++id, papel, nome, criadoEm',
      contratos: '++id, status, contatoId, faseConstrutiva, criadoEm',
      contrato_parcelas: '++id, contratoId, status, dataPrevista, lancamentoId',
      cronograma_fases: '++id, &faseId',
    })
    this.version(7).stores({
      decisoes: '++id, chave, faseId, travada, criadoEm',
    })
    this.version(8).stores({
      orcamentos_materiais: '++id, grupoId, status, material, lancamentoId, criadoEm',
    })
  }
}

/** Tabelas persistidas — usadas por backup, importação e limpeza. */
export const ALL_TABLES = [
  'obra_config',
  'topicos_progresso',
  'lancamentos_financeiros',
  'documentos',
  'documento_arquivos',
  'alertas',
  'diario',
  'orcamentos_materiais',
  'obra_pendencias',
  'contatos',
  'contratos',
  'contrato_parcelas',
  'cronograma_fases',
  'decisoes',
] as const

export type TableName = (typeof ALL_TABLES)[number]

export const db = new MinhaObraDB()
